<template>
  <div class="w-full h-full space-y-4">
    <Card>
      <CardHeader>
        <CardTitle>Transaction uploads</CardTitle>
        <CardDescription>Upload transactions via CSV and review previous uploads.</CardDescription>
      </CardHeader>
      <CardContent>
        <div class="space-y-3 max-w-xl">
          <div class="space-y-1">
            <label class="text-sm font-medium" for="upload-csv">CSV file</label>
            <input
              id="upload-csv"
              type="file"
              accept=".csv"
              ref="fileInput"
              @change="uploadFile"
              class="block w-full text-sm text-muted-foreground file:mr-3 file:rounded-md file:border-0 file:bg-muted file:px-3 file:py-2 file:text-sm file:font-medium"
            />
          </div>
          <div class="space-y-1">
            <label class="text-sm font-medium" for="uo-historical">Historical data</label>
            <Select v-model="historicalStr">
              <SelectTrigger id="uo-historical" class="w-full">
                <SelectValue placeholder="Select" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem v-for="o in trueOrFalse" :key="String(o.id)" :value="String(o.id)">{{ o.text }}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div class="space-y-1">
            <label class="text-sm font-medium" for="uo-enrichment">Enrich data</label>
            <Select v-model="enrichmentStr">
              <SelectTrigger id="uo-enrichment" class="w-full">
                <SelectValue placeholder="Select" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem v-for="o in trueOrFalse" :key="String(o.id)" :value="String(o.id)">{{ o.text }}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div class="flex items-center justify-between gap-2">
            <Button size="sm" variant="outline" @click="downloadTemplate()">
              <Download class="w-4 h-4" />Download template
            </Button>
            <Button size="sm" :disabled="uploadCSV == undefined" @click="uploadTransactions()">
              <Upload class="w-4 h-4" />Upload
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>

    <Card>
      <CardHeader>
        <CardTitle>Uploads history</CardTitle>
        <CardDescription>Previous CSV uploads and their status</CardDescription>
      </CardHeader>
      <CardContent>
        <DataTable
            :headers="headersTable1"
            :items="fileuploads"
            :loading="loading"
            :error="error"
            @retry="getFileUploads(serverOptions.page - 1, serverOptions.rowsPerPage)"
          v-model:server-options="serverOptions"
          :server-items-length="serverItemsLength"
        >
            <template #item-id="fileData">
              <CopyId :value="fileData.id" :max-width="120" />
            </template>
            <template #item-fileStatus="fileData">
              <Badge v-if="fileData.fileStatus === 'COMPLETED'" variant="success">{{ fileData.fileStatus }}</Badge>
              <Badge v-else-if="fileData.fileStatus === 'FAILED'" variant="destructive">{{ fileData.fileStatus }}</Badge>
              <Badge v-else variant="warning">{{ fileData.fileStatus }}</Badge>
            </template>
            <template #item-action="historicData">
              <div class="flex justify-end items-center gap-1">
                <Button
                  v-if="historicData.fileStatus == 'COMPLETED'"
                  size="sm"
                  @click="download(historicData.id)"
                >
                  <FileDown class="w-4 h-4" />Export cashback
                </Button>
              </div>
            </template>
            <template #item-createdOn="fileData">
              <div class="text-xs text-muted-foreground tabular-nums">
                {{ niceDateTime(fileData.createdOn) }}
              </div>
            </template>
        </DataTable>
      </CardContent>
    </Card>
  </div>
</template>

<script>
import { DateTime } from 'luxon'
import {
  Button,
  Badge,
  CopyId,
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
  Card, CardHeader, CardTitle, CardDescription, CardContent,
} from '@boogi/ui'
import { Upload, FileDown, Download } from 'lucide-vue-next'
import { DataTable } from '@boogi/ui'
import { breadcrumbState } from '@/stores/breadcrumb'

export default {
  name: 'TransactionUploadPage',
  components: {
    DataTable,
    Button,
    Badge,
    CopyId,
    Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
    Card, CardHeader, CardTitle, CardDescription, CardContent,
    Upload, FileDown, Download,
  },
  data() {
    return {
      fileuploads: [],
      serverOptions: {
        page: 1,
        rowsPerPage: 25,
      },
      uploadOptions: {
        historical: false,
        enrichment: true,
      },
      trueOrFalse: [
        { id: true, text: 'Yes' },
        { id: false, text: 'No' },
      ],
      uploadCSV: undefined,
      serverItemsLength: undefined,
      loading: true,
      error: false,
      headersTable1: [
        { text: 'Id', value: 'id' },
        { text: 'File name', value: 'fileName' },
        { text: 'Created On', value: 'createdOn' },
        { text: 'Status', value: 'fileStatus' },
        { text: 'Action', value: 'action' },
      ],
    }
  },
  created() {
    breadcrumbState.set([
      { label: 'Transaction processing', to: '/transaction-processing' },
      { label: 'Transaction uploads' },
    ])
    this.getFileUploads(0, 25)
  },
  unmounted() {
    breadcrumbState.clear()
  },
  computed: {
    historicalStr: {
      get() {
        return this.uploadOptions.historical ? 'true' : 'false'
      },
      set(value) {
        this.uploadOptions.historical = value === 'true'
      },
    },
    enrichmentStr: {
      get() {
        return this.uploadOptions.enrichment ? 'true' : 'false'
      },
      set(value) {
        this.uploadOptions.enrichment = value === 'true'
      },
    },
  },
  methods: {
    uploadFile: function () {
      this.uploadCSV = event.target.files[0]
    },

    downloadTemplate() {
      const headers = [
        'END_USER_ID',
        'TRANSACTION_ID',
        'TRANSACTION_DESCRIPTION',
        'AMOUNT',
        'CURRENCY',
        'TRANSACTION_DATE',
      ]
      const rows = [
        ['user-1234', 'txn-0001', 'TESCO STORES 1234 LONDON', '42.50', 'GBP', '2026-01-15'],
        ['user-5678', 'txn-0002', 'AMZNMktplace amazon.co.uk', '19.99', 'GBP', '2026-01-16'],
      ]

      const csvContent = [headers, ...rows]
        .map((row) => row.map((value) => `"${value}"`).join(','))
        .join('\n')

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.setAttribute('href', url)
      link.setAttribute('download', 'transactions_template.csv')
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
    },

    niceDateTime(dateObject) {
      return DateTime.fromMillis(dateObject).toLocaleString(DateTime.DATETIME_MED)
    },

    async download(id) {
      try {
        const token = localStorage.getItem('token')
        const apiUrl = localStorage.getItem('apiUrl') || process.env.VUE_APP_API_URL

        this.loading = true

        const response = await this.$apiFetch(
          `${apiUrl}/private/api/client/transaction-enrichment/upload-csv/${id}/download-cashback`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `${token}`,
            },
          }
        )

        if (response.ok) {
          const blob = await response.blob()
          const url = window.URL.createObjectURL(blob)

          const link = document.createElement('a')
          link.href = url
          link.setAttribute('download', `cashback_${id}.csv`)

          document.body.appendChild(link)
          link.click()

          document.body.removeChild(link)
        } else {
          console.error('Failed to download CSV')
        }
      } catch (error) {
        console.error('Error downloading CSV:', error)
      } finally {
        this.loading = false
      }
    },

    async uploadTransactions() {
      const formData = new FormData()
      const apiUrl = localStorage.getItem('apiUrl') || process.env.VUE_APP_API_URL

      formData.append('multipartFile', this.uploadCSV)

      try {
        const token = localStorage.getItem('token')
        this.loading = true

        const response = await this.$apiFetch(
          `${apiUrl}/private/api/client/transaction-enrichment/upload-csv?historic=${this.uploadOptions.historical}&enrichment=${this.uploadOptions.enrichment}`,
          {
            method: 'POST',
            body: formData,
            headers: {
              Authorization: `${token}`,
            },
          }
        )

        if (response.ok) {
          this.uploadCSV = undefined
          if (this.$refs.fileInput) this.$refs.fileInput.value = ''
          this.getFileUploads(0, 25)
        }
      } catch (error) {
        return
      }
    },

    async getFileUploads(page, size) {
      const token = localStorage.getItem('token')
      const apiUrl = localStorage.getItem('apiUrl') || process.env.VUE_APP_API_URL

      this.loading = true
      this.error = false

      try {
        const response = await this.$apiFetch(
          `${apiUrl}/private/api/client/transaction-enrichment/upload-csv?page=${page}&size=${size}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `${token}`,
            },
          }
        )

        if (response.ok) {
          var object = await response.json()
          this.fileuploads = object.content
          this.serverItemsLength = object.totalElements
        } else {
          this.error = true
        }
      } catch (e) {
        this.error = true
      } finally {
        this.loading = false
      }
    },
  },
  watch: {
    serverOptions: {
      deep: true,
      handler(val) {
        this.getFileUploads(val.page - 1, val.rowsPerPage)
      },
    },
  },
}
</script>
